import React, { useState, useEffect, useRef } from 'react';
import {
  FaPlay, FaTimes, FaExpand, FaCompress, FaArrowLeft, FaVideo, FaFilm
} from 'react-icons/fa';

const Videos = ({ onBack }) => {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const modalRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    // Scan the media folder for videos
    const mediaVideos = [
      { file: 'Intro-Video.mp4', title: 'Introduction to ItinerarySeek', description: 'Get started with ItinerarySeek and learn how to plan your perfect trip with AI-powered itinerary generation.' },
      { file: 'Day-Swap.mp4', title: 'Day Swap', description: 'Learn how to swap days in your itinerary to rearrange your travel schedule effortlessly.' },
      { file: 'Magic-Wand.mp4', title: 'Magic Wand', description: 'See how the Magic Wand feature lets group members apply suggestions to the itinerary instantly.' },
      { file: 'Explore-Stops-Along-The-Way.mp4', title: 'Explore Stops Along The Way', description: 'Discover hidden gems and attractions between your planned destinations using the explore feature.' },
    ];
    setVideos(mediaVideos);
  }, []);

  const openVideo = (video) => {
    setSelectedVideo(video);
    setIsFullscreen(false);
  };

  const closeVideo = () => {
    setSelectedVideo(null);
    setIsFullscreen(false);
  };

  const toggleFullscreen = () => {
    if (!modalRef.current) return;
    if (!isFullscreen) {
      if (modalRef.current.requestFullscreen) modalRef.current.requestFullscreen();
      else if (modalRef.current.webkitRequestFullscreen) modalRef.current.webkitRequestFullscreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && selectedVideo && !isFullscreen) closeVideo();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedVideo, isFullscreen]);

  const formatTitle = (filename) => {
    return filename.replace('.mp4', '').replace(/-/g, ' ');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-200 to-secondary-200 rounded-full opacity-30 blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-secondary-200 to-primary-200 rounded-full opacity-30 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <FaFilm className="absolute top-20 right-1/4 text-primary-100 text-6xl transform rotate-12 opacity-50" />
          <FaVideo className="absolute bottom-20 left-1/4 text-secondary-100 text-5xl opacity-40" />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
          {/* Back Button */}
          <button
            onClick={onBack}
            className="mb-8 flex items-center gap-2 text-neutral-600 hover:text-primary-500 transition-colors group"
          >
            <FaArrowLeft className="transform group-hover:-translate-x-1 transition-transform" />
            Back to How To
          </button>

          {/* Main Hero */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-primary-400"></span>
              <span className="text-primary-600 text-sm uppercase tracking-widest font-medium flex items-center gap-2">
                <FaVideo className="text-secondary-500" />
                Video Tutorials
              </span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-primary-400"></span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-primary-500 via-secondary-500 to-primary-500 bg-clip-text text-transparent">
                Watch & Learn
              </span>
            </h1>

            <p className="text-lg text-neutral-600 max-w-2xl mx-auto leading-relaxed">
              Watch video tutorials to see ItinerarySeek features in action. Click any video to play.
            </p>
          </div>
        </div>
      </div>

      {/* Video Cards Grid */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video, index) => (
            <div
              key={index}
              onClick={() => openVideo(video)}
              className="bg-white rounded-2xl border border-neutral-100 shadow-card hover:shadow-card-hover hover:border-primary-200 transition-all duration-300 cursor-pointer group overflow-hidden"
            >
              {/* Video Thumbnail */}
              <div className="relative aspect-video bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center overflow-hidden">
                <video
                  src={`/media/${video.file}`}
                  className="w-full h-full object-cover"
                  muted
                  preload="metadata"
                />
                {/* Play overlay */}
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-white/90 group-hover:bg-white group-hover:scale-110 transition-all duration-300 flex items-center justify-center shadow-lg">
                    <FaPlay className="text-primary-500 text-xl ml-1" />
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-5">
                <h3 className="font-semibold text-lg text-neutral-800 group-hover:text-primary-600 transition-colors mb-2">
                  {video.title}
                </h3>
                <p className="text-neutral-500 text-sm leading-relaxed">
                  {video.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {videos.length === 0 && (
          <div className="text-center py-12">
            <FaVideo className="text-4xl text-neutral-300 mx-auto mb-4" />
            <p className="text-neutral-500">No videos available yet.</p>
          </div>
        )}
      </div>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) closeVideo(); }}
        >
          <div
            ref={modalRef}
            className={`relative bg-neutral-900 rounded-2xl overflow-hidden shadow-2xl ${
              isFullscreen ? 'w-full h-full rounded-none' : 'w-[90vw] max-w-4xl'
            }`}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-neutral-800">
              <h3 className="text-white font-medium truncate pr-4">
                {selectedVideo.title}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleFullscreen}
                  className="w-8 h-8 rounded-lg bg-neutral-700 hover:bg-neutral-600 text-white flex items-center justify-center transition-colors"
                  title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                  {isFullscreen ? <FaCompress className="text-sm" /> : <FaExpand className="text-sm" />}
                </button>
                <button
                  onClick={closeVideo}
                  className="w-8 h-8 rounded-lg bg-red-500/80 hover:bg-red-500 text-white flex items-center justify-center transition-colors"
                  title="Close"
                >
                  <FaTimes className="text-sm" />
                </button>
              </div>
            </div>

            {/* Video Player */}
            <div className={isFullscreen ? 'h-[calc(100%-52px)]' : ''}>
              <video
                ref={videoRef}
                src={`/media/${selectedVideo.file}`}
                className="w-full h-full"
                controls
                autoPlay
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Videos;
